import { useAuth0 } from '@auth0/auth0-react';
import { RequestActionType } from '@strata/react-chat';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { PayerRequestWritePermissions } from 'services/constants';
import { handleError } from 'services/Error';
import { useAppSelector } from 'store';
import { useGetPayerUserWithPermNameMapQuery } from 'store/api/authApi';
import {
    type PayerRequestActionDialogProps,
    usePostRequestActivityMutation,
} from 'store/api/payerRequestApi';

const Title = 'Select/Change Assignment';

/** UPN value for unassigned since undefined would break the required validation */
const UNASSIGNED = 'Unassigned';

export const UpdateAssignmentDialog = ({
    onClose,
    payerId,
    requestDetails,
}: PayerRequestActionDialogProps) => {
    const { user } = useAuth0();
    const [postRequestActivity] = usePostRequestActivityMutation();
    const requestType = requestDetails?.requestType.toString().toLowerCase();
    const { pbmOnly: requestIsPBMOnly } = requestDetails;

    const { permissions } = useAppSelector((state) => state.auth);
    const currentUserHasPBMOnlyAccess = permissions.includes(
        PayerRequestWritePermissions.WRITE_PAYER_REQUEST_PBM_ONLY,
    );

    const { data: clientUsers, isFetching: payerUsersFetching } =
        useGetPayerUserWithPermNameMapQuery({
            segmentId: requestDetails.segmentId,
            permission: `write:payer_request_${requestType}`,
            includePBMOnlyAccess: currentUserHasPBMOnlyAccess,
        });

    const payerAssigneeOptions = [
        { value: UNASSIGNED, name: UNASSIGNED, hasPBMOnlyAccess: true },
        { value: user?.email, name: 'Assign to Me', hasPBMOnlyAccess: true },
        ...Object.entries({ ...clientUsers })
            .filter(([upn]) => upn !== user?.email)
            .filter(([upn]) => !upn.endsWith('@freemarkethealth.com'))
            .map(([upn, { full_name: fullName, has_pbm_only_access: hasPBMOnlyAccess }]) => ({
                value: upn,
                name: fullName,
                hasPBMOnlyAccess,
            })),
    ];

    return (
        <Formik
            initialValues={{ payerAssignment: requestDetails.payerAssignedUpn ?? UNASSIGNED }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({ payerAssignment: Yup.string().required() })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await postRequestActivity({
                        payerId,
                        requestId: requestDetails.requestId,
                        actionType: RequestActionType.ASSIGN_CL,
                        actionDetail:
                            values.payerAssignment !== UNASSIGNED
                                ? values.payerAssignment
                                : undefined,
                    }).unwrap();

                    SuccessToast({ message: 'Assignment updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update assignment. Please try again.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={600}
                    isOpen
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Assign Request"
                    isPrimaryButtonDisabled={
                        !(formik.isValid && formik.dirty) || formik.isSubmitting
                    }
                    dialogContent={
                        <SuggestSingleSelectFormik
                            id="payerAssignment"
                            label="Payer Assignee"
                            required
                            isLoading={payerUsersFetching}
                            values={payerAssigneeOptions.filter((option) =>
                                requestIsPBMOnly === true ? option?.hasPBMOnlyAccess : true,
                            )}
                            selectedItem={payerAssigneeOptions.find(
                                ({ value }) => value === formik.values.payerAssignment,
                            )}
                            onChange={(newVal) => {
                                void formik.setFieldValue('payerAssignment', newVal?.value);
                            }}
                        />
                    }
                />
            )}
        </Formik>
    );
};

UpdateAssignmentDialog.Title = Title;
